import { data, find, findAll } from './dom';
import { readStored, removeStored, writeStored } from './storage';
import { miniSprite } from './theme-images';
import type { Toast } from './toast';

// Tema kalendara, sa zaglavljem firme ili bez njega.
export interface ThemeItem {
  readonly theme: string;
  readonly name: string;
  readonly withHeader: boolean;
  readonly quantity: number;
}

// Proizvod iz kataloga (adresa stranice), sa bojom izabranom u galeriji i sličicom za spisak.
export interface ProductItem {
  readonly product: string;
  readonly name: string;
  readonly color: string | null;
  readonly image: string | null;
  readonly quantity: number;
}

export type CartItem = ThemeItem | ProductItem;

export interface Cart {
  add(item: CartItem): void;
  // Posle poslate porudžbine (stranica potvrde).
  clear(): void;
  // Stavke kao redovi poruke („Zidni kalendar 2027 „Kamioni“, 25 kom, sa zaglavljem firme“), za spisak na stranici porudžbine.
  lines(): string[];
  // Spisak kakav pregledač pamti, za skriveno polje porudžbine (server ga proverava i pretvara u stavke).
  json(): string;
}

const storageKey = 'printarnica-korpa';
const slugPattern = /^[a-z0-9-]+$/;
// Sličica proizvoda je slika sa adrese sajta (bez navodnika, zagrada i razmaka), jer ide u CSS url().
const imagePattern = /^\/[\w./?=-]+$/;

const isTheme = (item: CartItem): item is ThemeItem => 'theme' in item;

// Ista tema sa istim izborom zaglavlja, odnosno isti proizvod u istoj boji, je jedna stavka sa zbirnom količinom.
const sameEntry = (first: CartItem, second: CartItem): boolean =>
  isTheme(first) && isTheme(second)
    ? first.theme === second.theme && first.withHeader === second.withHeader
    : !isTheme(first) && !isTheme(second) && first.product === second.product && first.color === second.color;

const filled = (parts: (string | null)[]): string[] => parts.filter((part): part is string => part !== null);

// Porudžbina je spisak tema kalendara i proizvoda sa količinama, bez cena (web/ARCHITECTURE.md §3). Pamti se u
// pregledaču i ide na stranicu porudžbine (lib/order.ts), a može da se pošalje i kao poruka: na Viber se spisak kopira
// (Viber ne prima tekst kroz adresu ćaskanja), WhatsApp ga dobija kao tekst poruke u adresi (wa.me/…?text=), a e-mail u telu
// poruke. Obe adrese se sastavljaju ponovo posle svake izmene korpe.
export function enhanceCart(toast: Toast): Cart {
  const dialog = find(document, 'cart', HTMLDialogElement);
  const list = find(dialog, 'cart-items', HTMLUListElement);
  const empty = find(dialog, 'cart-empty', HTMLElement);
  const total = find(dialog, 'cart-total', HTMLElement);
  const actions = find(dialog, 'cart-actions', HTMLElement);
  const viber = find(dialog, 'cart-viber', HTMLAnchorElement);
  const whatsApp = find(dialog, 'cart-whatsapp', HTMLAnchorElement);
  const email = find(dialog, 'cart-email', HTMLAnchorElement);
  const template = find(dialog, 'cart-item-template', HTMLTemplateElement);
  const counts = findAll(document, 'cart-count', HTMLElement);
  const whatsAppAddress = whatsApp.href;
  const emailAddress = email.href;
  const text = {
    intro: data(dialog, 'messageIntro'),
    calendar: data(dialog, 'calendar'),
    withHeader: data(dialog, 'withHeader'),
    withoutHeader: data(dialog, 'withoutHeader'),
    unit: data(dialog, 'unit'),
    total: data(dialog, 'total'),
    subject: data(dialog, 'emailSubject'),
    added: data(dialog, 'added'),
    copied: data(dialog, 'copied'),
    copyFailed: data(dialog, 'copyFailed'),
  };

  let items = readItems();

  const sum = (): number => items.reduce((count, item) => count + item.quantity, 0);
  const pieces = (item: CartItem): string => `${item.quantity} ${text.unit}`;

  // Uz količinu ide zaglavlje teme, odnosno boja proizvoda (proizvod bez izabrane boje nema dodatak).
  const detail = (item: CartItem): string | null =>
    isTheme(item) ? (item.withHeader ? text.withHeader : text.withoutHeader) : item.color;

  const line = (item: CartItem): string =>
    filled([isTheme(item) ? `${text.calendar} „${item.name}“` : item.name, pieces(item), detail(item)]).join(', ');

  const message = (): string =>
    [text.intro, ...items.map(item => `- ${line(item)}`), `${text.total} ${sum()} ${text.unit}`].join('\n');

  const json = (): string => JSON.stringify(items);

  const save = (): void => writeStored(storageKey, json());

  const row = (item: CartItem): HTMLLIElement => {
    const fragment = document.importNode(template.content, true);
    const thumb = find(fragment, 'cart-item-thumb', HTMLElement);
    if (isTheme(item)) {
      thumb.style.setProperty('--mini-sprite', `url("${miniSprite(item.theme)}")`);
    } else {
      thumb.classList.add('mini-sheet--product');
      if (item.image !== null) thumb.style.setProperty('--mini-sprite', `url("${item.image}")`);
    }
    find(fragment, 'cart-item-name', HTMLElement).textContent = item.name;
    find(fragment, 'cart-item-detail', HTMLElement).textContent = filled([pieces(item), detail(item)]).join(' · ');
    // Ista tema sa zaglavljem i bez njega (i isti proizvod u dve boje) su dva reda, pa uz naziv ide i izbor.
    find(fragment, 'cart-item-remove-name', HTMLElement).textContent =
      ` ${filled([item.name, detail(item)]).join(', ')}`;
    $(find(fragment, 'cart-item-remove', HTMLButtonElement)).on('click', () => remove(item));
    return find(fragment, 'cart-item', HTMLLIElement);
  };

  const render = (): void => {
    const count = String(sum());
    counts.forEach(element => {
      element.textContent = count;
    });
    total.textContent = count;
    list.replaceChildren(...items.map(row));
    empty.hidden = items.length > 0;
    actions.hidden = items.length === 0;
    whatsApp.href = `${whatsAppAddress}?text=${encodeURIComponent(message())}`;
    email.href = `${emailAddress}?subject=${encodeURIComponent(text.subject)}&body=${encodeURIComponent(message())}`;
  };

  // Dugme koje je kupac dodirnuo nestaje sa svojim redom, pa fokus ide na „Ukloni“ reda koji je sada na istom mestu (ili
  // poslednjeg), a kad je porudžbina prazna na poruku o tome: čitač ekrana i tastatura ne gube mesto u fioci. Fokus menja samo
  // uklanjanje, jer render crta spisak i kad se stavka doda ili kad se korpa isprazni posle porudžbine.
  const remove = (removed: CartItem): void => {
    const index = items.indexOf(removed);
    items = items.filter(item => item !== removed);
    save();
    render();
    const buttons = findAll(list, 'cart-item-remove', HTMLButtonElement);
    (buttons[Math.min(index, buttons.length - 1)] ?? empty).focus();
  };

  $(viber).on('click', () => {
    navigator.clipboard.writeText(message()).then(
      () => toast.show(text.copied),
      () => toast.show(text.copyFailed)
    );
  });

  findAll(document, 'cart-open', HTMLButtonElement).forEach(opener => {
    opener.hidden = false;
    $(opener).on('click', () => dialog.showModal());
  });
  // Zatvara je dugme u zaglavlju fioke, a na koracima porudžbine i glavno dugme („Nazad na porudžbinu“).
  findAll(dialog, 'cart-close', HTMLButtonElement).forEach(closer => {
    $(closer).on('click', () => dialog.close());
  });
  $(dialog).on('click', event => {
    if (event.target === dialog) dialog.close();
  });

  render();

  return {
    add(added) {
      // Sličica koja nije slika sa sajta se ne pamti, da stavka ostane ispravna i posle ponovnog učitavanja.
      const item =
        isTheme(added) || added.image === null || imagePattern.test(added.image) ? added : { ...added, image: null };
      const existing = items.find(entry => sameEntry(entry, item));
      items = existing
        ? items.map(entry => (entry === existing ? { ...entry, quantity: entry.quantity + item.quantity } : entry))
        : [...items, item];
      save();
      render();
      toast.show(`${text.added} ${item.name}, ${pieces(item)}`);
    },
    clear() {
      items = [];
      removeStored(storageKey);
      render();
    },
    lines: () => items.map(line),
    json,
  };
}

function readItems(): CartItem[] {
  const stored = readStored(storageKey);
  return stored === null ? [] : parseItems(stored);
}

// Spisak iz pregledača može da bude oštećen ili iz starije verzije sajta; zadržavaju se samo ispravne stavke.
function parseItems(json: string): CartItem[] {
  let value: unknown;
  try {
    value = JSON.parse(json);
  } catch (error) {
    if (error instanceof SyntaxError) return [];
    throw error;
  }
  if (!Array.isArray(value)) return [];
  const entries: unknown[] = value;
  return entries.filter(isCartItem);
}

function isCartItem(value: unknown): value is CartItem {
  if (typeof value !== 'object' || value === null) return false;
  const named = 'name' in value && typeof value.name === 'string' && value.name !== '';
  const counted =
    'quantity' in value && typeof value.quantity === 'number' && Number.isInteger(value.quantity) && value.quantity > 0;
  if (!named || !counted) return false;
  if ('theme' in value) {
    return (
      typeof value.theme === 'string' &&
      slugPattern.test(value.theme) &&
      'withHeader' in value &&
      typeof value.withHeader === 'boolean'
    );
  }
  return (
    'product' in value &&
    typeof value.product === 'string' &&
    slugPattern.test(value.product) &&
    'color' in value &&
    (value.color === null || (typeof value.color === 'string' && value.color !== '')) &&
    'image' in value &&
    (value.image === null || (typeof value.image === 'string' && imagePattern.test(value.image)))
  );
}
